import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  Chip,
  TextField,
  Paper,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";
import { printPackageLabel } from "../../Utils/packageLabel";
import { statusColor, paymentColor, paymentLabel } from "./index.jsx";

// One order from /api/orders/:id (admin view): every seller's part with its
// own status, driver, delivery code and payout, plus refunds and history.

const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
const STATUSES = ["Processing", "Ready to Pickup", "Shipped", "Delivered"];
const OPEN = (part) => !["Delivered", "Cancelled", "Refunded"].includes(part.status);
const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const EVENT_LABEL = {
  created: "Checkout started",
  paid: "Paid",
  payment_review: "Payment amount did not match",
  expired: "Checkout expired",
  status_changed: "Status changed",
  delivery_code_sent: "Delivery code sent",
  driver_assigned: "Driver assigned",
  driver_accepted: "Driver accepted",
  driver_declined: "Driver declined",
  id_photo_taken: "ID photo taken",
  id_photo_viewed: "ID photo opened",
  delivered: "Delivered",
  payout_released: "Seller payout",
  delivery_fee_adjusted: "Delivery fee lowered",
  driver_paid: "Driver paid",
  refunded: "Refunded",
  disputed: "Card dispute opened",
  dispute_closed: "Card dispute closed",
  note: "Delivery note",
};

async function call(path, method = "GET", body) {
  const res = await authFetch(path, { method, ...(body ? { body: JSON.stringify(body) } : {}) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status === false) throw new Error(json.message || `Request failed (${res.status})`);
  return json;
}

function ViewById() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [drivers, setDrivers] = useState({});
  const [chosenDriver, setChosenDriver] = useState({});
  const [chosenStatus, setChosenStatus] = useState({});
  const [note, setNote] = useState("");
  const [refund, setRefund] = useState(null); // { fulfillmentId|null, reason }
  const [feeEdit, setFeeEdit] = useState(null); // { part, dollars }
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const notify = (message, severity = "success") => setToast({ open: true, message, severity });

  const load = useCallback(async () => {
    try {
      const json = await call(`/api/orders/${id}`);
      setOrder(json.order);
      setNote(json.order.deliveryNote || "");
      setError("");
      return json.order;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      const loaded = await load();
      if (!loaded) return;
      for (const part of loaded.parts) {
        if (part.deliveryMethod === "pickup" || !OPEN(part)) continue;
        call(`/api/orders/parts/${part.id}/drivers`)
          .then((json) => setDrivers((prev) => ({ ...prev, [part.id]: json.drivers })))
          .catch(() => {});
      }
    })();
  }, [load]);

  const act = async (key, fn, success) => {
    setBusy(key);
    try {
      const json = await fn();
      notify(json?.message || success);
      await load();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setBusy("");
    }
  };

  // The photo needs the admin's login, so it cannot be a plain link.
  const openIdPhoto = async (part) => {
    const opened = window.open("", "_blank");
    try {
      const res = await authFetch(`/api/orders/parts/${part.id}/id-photo`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Could not open the ID photo");
      opened.location = URL.createObjectURL(await res.blob());
      await load();
    } catch (err) {
      opened.close();
      notify(err.message, "error");
    }
  };

  const assign = (part) =>
    act(`assign-${part.id}`, () => call(`/api/orders/parts/${part.id}/assign`, "POST", { driverId: chosenDriver[part.id] }), "Driver assigned");
  const setStatus = (part) =>
    act(`status-${part.id}`, () => call(`/api/orders/parts/${part.id}/status`, "PATCH", { status: chosenStatus[part.id] }), "Status updated");
  const resend = (part) => act(`code-${part.id}`, () => call(`/api/orders/parts/${part.id}/resend-code`, "POST"), "Code sent");
  const releasePayout = (part) =>
    act(`payout-${part.id}`, () => call(`/api/orders/parts/${part.id}/release-payout`, "POST"), "Payout sent");
  const saveFee = () =>
    act(
      `fee-${feeEdit.part.id}`,
      async () => {
        const json = await call(`/api/orders/parts/${feeEdit.part.id}/delivery-fee`, "POST", {
          finalCents: Math.round(Number(feeEdit.dollars) * 100),
        });
        setFeeEdit(null);
        return json;
      },
      "Delivery fee updated"
    );
  const driverPaid = (part) =>
    act(`driverpaid-${part.id}`, () => call(`/api/orders/parts/${part.id}/driver-paid`, "POST"), "Driver marked as paid");
  const saveNote = () => act("note", () => call(`/api/orders/${id}/note`, "PATCH", { deliveryNote: note }), "Note saved");
  const sendRefund = () =>
    act(
      "refund",
      async () => {
        const json = await call(`/api/orders/${id}/refund`, "POST", {
          fulfillmentId: refund.fulfillmentId || undefined,
          reason: refund.reason,
        });
        setRefund(null);
        return json;
      },
      "Refund sent"
    );

  const printLabel = async (part) => {
    setBusy(`label-${part.id}`);
    try {
      const s = order.shipping || {};
      await printPackageLabel(
        {
          id: order.id,
          deliveryMethod: part.deliveryMethod,
          contactname: s.contactName || order.buyer?.name,
          mobilenumber: s.phone,
          line1: s.line1,
          line2: s.line2,
          city: s.city,
          state: s.state,
          zipCode: s.zipCode,
          country: s.country,
        },
        {
          products: part.items.map((i) => ({ productName: i.title, quantity: i.quantity })),
          sellerName: part.seller?.name,
          sellerPhone: part.seller?.phone,
          sellerLocation: part.seller?.location ? `${part.seller.location.lat},${part.seller.location.lng}` : "",
        }
      );
    } catch (err) {
      notify(err?.message || "Failed to open package label", "error");
    } finally {
      setBusy("");
    }
  };

  if (error) {
    return (
      <Box m="20px">
        <Alert severity="error" action={<Button onClick={() => navigate("/ordermanagement")}>Back</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }
  if (!order) {
    return (
      <Box m="40px" display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const paid = ["paid", "partially_refunded", "disputed"].includes(order.paymentStatus);
  const left = order.totalCents - order.refundedCents;
  const ship = order.shipping || {};

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Header title={`Order #${order.id}`} subtitle={`Placed ${formatDate(order.createdAt)}${order.paidAt ? ` · paid ${formatDate(order.paidAt)}` : ""}`} />
        <Stack direction="row" spacing={1}>
          <Chip label={paymentLabel(order.paymentStatus)} color={paymentColor(order.paymentStatus)} />
          <Chip label={order.status} color={statusColor(order.status)} />
        </Stack>
      </Box>

      {order.paymentStatus === "review" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The amount Stripe collected did not match this order, so it was not fulfilled. Check the payment in Stripe ({order.paymentIntentId || "no payment id"}) and refund it or contact the buyer.
        </Alert>
      )}
      {order.paymentStatus === "disputed" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The buyer disputed this card payment. Seller payouts are on hold until Stripe settles it.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h5" gutterBottom>Money</Typography>
            <Row label="Items" value={money(order.subtotalCents)} />
            {order.taxCents > 0 && <Row label="Tax and fees" value={money(order.totalCents - order.subtotalCents)} />}
            <Row label="Charged" value={money(order.totalCents)} strong />
            {order.refundedCents > 0 && <Row label="Refunded" value={`− ${money(order.refundedCents)}`} />}
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">Stripe payment: {order.paymentIntentId || "—"}</Typography>
            {paid && left > 0 && (
              <Button sx={{ mt: 2 }} color="error" variant="outlined" fullWidth onClick={() => setRefund({ fulfillmentId: null, reason: "" })}>
                Refund whole order ({money(left)})
              </Button>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h5" gutterBottom>Buyer</Typography>
            <Typography>{order.buyer?.name || "—"}</Typography>
            <Typography variant="body2">{order.buyer?.email}</Typography>
            {["other", "business"].includes(order.recipient?.type) ? (
              <Alert severity="info" sx={{ my: 1, py: 0 }}>
                {order.recipient.type === "business" ? "For the business" : "For"} <b>{order.recipient.name}</b> · {order.recipient.email} · WhatsApp {order.recipient.phone}
              </Alert>
            ) : (
              <Typography variant="body2">WhatsApp / phone: {order.recipient?.phone || ship.phone || "none"}</Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {order.deliveryMethod === "pickup" ? "Collects from the seller" : "Deliver to"}
            </Typography>
            {order.deliveryMethod !== "pickup" && (
              <Typography variant="body2">
                {[ship.line1, ship.line2, ship.city, ship.state, ship.zipCode, ship.country].filter(Boolean).join(", ") || "No address"}
              </Typography>
            )}
            {order.dropoff && (
              <Typography variant="body2">
                <a href={`https://www.google.com/maps?q=${order.dropoff.lat},${order.dropoff.lng}`} target="_blank" rel="noopener noreferrer">
                  Drop-off pin on the map
                </a>
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h5" gutterBottom>Note for the driver</Typography>
            <TextField fullWidth multiline minRows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Call before arriving" />
            <Button sx={{ mt: 1 }} variant="contained" color="secondary" onClick={saveNote} disabled={busy === "note"}>
              Save note
            </Button>
          </Paper>
        </Grid>

        {order.parts.map((part) => {
          const list = drivers[part.id] || [];
          const pickup = part.deliveryMethod === "pickup";
          return (
            <Grid item xs={12} key={part.id}>
              <Paper sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography variant="h5">
                    {part.seller?.name || "Seller"} <Typography component="span" color="text.secondary">· part {part.id}</Typography>
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip label={pickup ? "Pickup" : "Delivery"} variant="outlined" size="small" />
                    <Chip label={part.status} color={statusColor(part.status)} size="small" />
                    {part.payoutHold && <Chip label="Payout on hold" color="warning" size="small" />}
                  </Stack>
                </Box>

                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={4}>
                    {part.items.map((item) => (
                      <Typography key={item.productId} variant="body2">
                        {item.quantity} × {item.title} · ${Number(item.unitPrice).toFixed(2)}
                      </Typography>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Row label="Items" value={money(part.itemsCents)} />
                    <Row label="Commission" value={money(part.commissionCents)} />
                    <Row label="Seller gets" value={money(part.sellerAmountCents)} strong />
                    {part.deliveryFeeCents > 0 && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Row
                          label={`Delivery (${part.vehicleType || "vehicle"}, ${part.distanceKm ?? "?"} km) paid`}
                          value={money(part.deliveryFeeCents)}
                        />
                        {part.deliveryFeeFinalCents != null && (
                          <Row label="Final delivery fee" value={`${money(part.deliveryFeeFinalCents)} (${money(part.deliveryFeeCents - part.deliveryFeeFinalCents)} refunded)`} />
                        )}
                        <Row
                          label="Driver pay"
                          value={part.driverPayCents != null ? `${money(part.driverPayCents)}${part.driverPaidAt ? " · paid" : " · owed"}` : "after delivery"}
                        />
                      </>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {part.payoutReleasedAt ? `Paid out ${formatDate(part.payoutReleasedAt)}` : "Paid out when delivered"}
                    </Typography>
                    {part.hasIdPhoto && (
                      <Box mt={1}>
                        <Button size="small" variant="outlined" onClick={() => openIdPhoto(part)}>
                          Open recipient's ID photo
                        </Button>
                        <Typography variant="caption" color="text.secondary" display="block">
                          For claims only. Each opening is recorded in the history.
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {pickup ? (
                      <Typography variant="body2" color="text.secondary">
                        The buyer collects this from the seller, who confirms it with the buyer's code.
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Driver</Typography>
                        {part.driver ? (
                          <Typography variant="body2" gutterBottom>
                            {part.driver.name} · {part.driver.phone} ·{" "}
                            <b>{part.driverStatus === "accepted" ? "accepted" : part.driverStatus === "declined" ? "declined" : "waiting to accept"}</b>
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="error" gutterBottom>No driver yet</Typography>
                        )}
                        {OPEN(part) && paid && (
                          <Stack direction="row" spacing={1}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>{part.driver ? "Change driver" : "Assign driver"}</InputLabel>
                              <Select
                                label={part.driver ? "Change driver" : "Assign driver"}
                                value={chosenDriver[part.id] || ""}
                                onChange={(e) => setChosenDriver((prev) => ({ ...prev, [part.id]: e.target.value }))}
                              >
                                {list.map((d) => (
                                  <MenuItem key={d.userId} value={d.userId}>
                                    {d.name} {d.available ? "" : "(unavailable)"} {d.distanceKm != null ? `· ${d.distanceKm} km from seller` : ""}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button variant="contained" color="secondary" disabled={!chosenDriver[part.id] || busy === `assign-${part.id}`} onClick={() => assign(part)}>
                              Assign
                            </Button>
                          </Stack>
                        )}
                      </>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Stack spacing={1}>
                      {OPEN(part) && paid && (
                        <Stack direction="row" spacing={1}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Set status</InputLabel>
                            <Select
                              label="Set status"
                              value={chosenStatus[part.id] || ""}
                              onChange={(e) => setChosenStatus((prev) => ({ ...prev, [part.id]: e.target.value }))}
                            >
                              {STATUSES.filter((s) => s !== part.status && (pickup ? s !== "Shipped" : s !== "Ready to Pickup")).map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button variant="contained" color="secondary" disabled={!chosenStatus[part.id] || busy === `status-${part.id}`} onClick={() => setStatus(part)}>
                            Save
                          </Button>
                        </Stack>
                      )}
                      {["Shipped", "Ready to Pickup"].includes(part.status) && (
                        <Button variant="outlined" color="secondary" disabled={busy === `code-${part.id}`} onClick={() => resend(part)}>
                          Resend delivery code to buyer
                        </Button>
                      )}
                      {part.status === "Delivered" && !part.payoutReleasedAt && (
                        <Button variant="outlined" color="secondary" disabled={busy === `payout-${part.id}`} onClick={() => releasePayout(part)}>
                          Retry seller payout
                        </Button>
                      )}
                      {part.deliveryFeeCents > 0 && paid && !["Refunded", "Cancelled"].includes(part.status) && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => setFeeEdit({ part, dollars: ((part.deliveryFeeFinalCents ?? part.deliveryFeeCents) / 100).toFixed(2) })}
                        >
                          Lower delivery fee (refund difference)
                        </Button>
                      )}
                      {part.driverPayCents != null && !part.driverPaidAt && (
                        <Button variant="outlined" color="secondary" disabled={busy === `driverpaid-${part.id}`} onClick={() => driverPaid(part)}>
                          Mark driver paid ({money(part.driverPayCents)})
                        </Button>
                      )}
                      <Button variant="outlined" color="secondary" disabled={busy === `label-${part.id}`} onClick={() => printLabel(part)}>
                        Print package label
                      </Button>
                      {paid && !["Refunded", "Cancelled"].includes(part.status) && order.parts.length > 1 && (
                        <Button variant="outlined" color="error" onClick={() => setRefund({ fulfillmentId: part.id, reason: "" })}>
                          Refund this seller's items ({money(part.itemsCents)})
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>History</Typography>
            {(order.events || []).map((e) => (
              <Box key={e.id} py={0.75} borderBottom="1px solid rgba(128,128,128,.2)">
                <Typography variant="body2">
                  <b>{EVENT_LABEL[e.type] || e.type}</b>
                  {e.toStatus ? ` → ${e.toStatus}` : ""}
                  {e.fulfillmentId ? ` (part ${e.fulfillmentId})` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(e.createdAt)} · {e.actorType}{e.actorId ? ` #${e.actorId}` : ""}{e.note ? ` · ${e.note}` : ""}
                </Typography>
              </Box>
            ))}
            {!(order.events || []).length && <Typography variant="body2">No history recorded.</Typography>}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Seller payouts</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Part</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Stripe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(order.payouts || []).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.fulfillmentId || "—"}</TableCell>
                    <TableCell>{money(p.amountCents)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.status} color={p.status === "succeeded" ? "success" : p.status === "failed" ? "error" : "default"} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{p.transferId || p.errorMessage || "—"}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!(order.payouts || []).length && <Typography variant="body2" mt={1}>No payouts queued.</Typography>}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={Boolean(refund)} onClose={() => setRefund(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{refund?.fulfillmentId ? "Refund this seller's items" : "Refund the whole order"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            The money goes back to the buyer's card. Unsent seller payouts are cancelled, sent ones are taken back, and undelivered items go back into stock.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label="Reason (shown in the history and the buyer's email)"
            value={refund?.reason || ""}
            onChange={(e) => setRefund((r) => ({ ...r, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefund(null)} color="secondary">Cancel</Button>
          <Button color="error" variant="contained" disabled={!refund?.reason?.trim() || busy === "refund"} onClick={sendRefund}>
            Send refund
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(feeEdit)} onClose={() => setFeeEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Final delivery fee</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            The buyer paid an estimate of {feeEdit ? money(feeEdit.part.deliveryFeeCents) : ""}. Enter what the delivery really cost; the difference is refunded to the buyer's card and the driver earns the new amount. It cannot be higher than the estimate.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            type="number"
            label="Final fee (USD)"
            inputProps={{ min: 0, step: "0.01" }}
            value={feeEdit?.dollars || ""}
            onChange={(e) => setFeeEdit((f) => ({ ...f, dollars: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeEdit(null)} color="secondary">Cancel</Button>
          <Button variant="contained" color="secondary" disabled={!feeEdit || busy === `fee-${feeEdit?.part.id}`} onClick={saveFee}>
            Save and refund
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function Row({ label, value, strong }) {
  return (
    <Box display="flex" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={strong ? 700 : 400}>{value}</Typography>
    </Box>
  );
}

export default ViewById;
